import { UPDATE_CONTINUE_WATCHING_ITEMS } from 'app/actions/continueWatching';
import { useLocalStorage } from 'app/hooks/useLocalStorage';
import { LOCAL_STORAGE_KEY } from 'app/utils/persistentState';
import React, { useEffect, useMemo } from 'react';
import { useQuery } from 'urql';
import CONTINUE_WATCHING_MIGRATION from './ContinueWatchingMigration.graphql';

type Props = {
    loadLocalStorage: any;
    oldItems: any[];
    setData: any;
    oldPersitentState: any;
};

const ContinueWatchingMigration = ({ oldPersitentState, setData, loadLocalStorage, oldItems }: Props) => {
    const [result] = useQuery({
        query: CONTINUE_WATCHING_MIGRATION,
        variables: {
            ids: oldItems.filter((item) => item.videoId).map((item) => item.videoId),
        },
        context: useMemo(
            () => ({
                preferGetMethod: false,
            }),
            []
        ),
    });

    const { data, fetching, error } = result;

    const listables = data?.listables;
    useEffect(() => {
        if (!listables) {
            if (!fetching || error) {
                loadLocalStorage();
            }

            return;
        }

        const newData = oldItems
            .filter((oldItem) => oldItem.videoId)
            .map((oldItem) => {
                const newItem = listables.find(
                    (listable) =>
                        listable.id === oldItem.videoId ||
                        (listable.legacyId &&
                            parseInt(listable.legacyId, 10) === parseInt(oldItem.videoId, 10))
                );

                if (!newItem) {
                    return undefined;
                }

                return {
                    barn: oldItem.barn,
                    timestamp: oldItem.timestamp,
                    videoId: newItem.svtId,
                    titleArticleId:
                        newItem.parent?.__typename !== 'Single' || oldItem.titleArticleId
                            ? newItem.parent?.svtId
                            : undefined,
                    movie: oldItem.movie,
                    position: oldItem.position,
                    ended: oldItem.ended,
                };
            })
            .filter((newItem) => newItem);

        setData({
            ...oldPersitentState,
            [UPDATE_CONTINUE_WATCHING_ITEMS]: {
                ...oldPersitentState[UPDATE_CONTINUE_WATCHING_ITEMS],
                items: newData,
            },
        });

        loadLocalStorage();
    }, [oldItems, listables]); // eslint-disable-line

    return null;
};

type ContainerProps = {
    loadLocalStorage: any;
};

const ContinueWatchingMigrationContainer = ({ loadLocalStorage }: ContainerProps) => {
    const [oldPersitentState] = useLocalStorage('persistent_state', {});
    const [newPersitentState, setData] = useLocalStorage(LOCAL_STORAGE_KEY, {});
    const newItems = newPersitentState[UPDATE_CONTINUE_WATCHING_ITEMS];
    const oldItems = oldPersitentState[UPDATE_CONTINUE_WATCHING_ITEMS]?.items || [];
    const shouldMigrate = !newItems && oldItems.length;
    useEffect(() => {
        // If continue watching is migrated, load redux directly
        if (!shouldMigrate) {
            if (!oldItems.length && !Object.keys(newPersitentState).length) {
                setData({
                    ...oldPersitentState,
                });
            }

            loadLocalStorage();
        }
    }, []); // eslint-disable-line

    if (shouldMigrate) {
        return (
            <ContinueWatchingMigration
                loadLocalStorage={loadLocalStorage}
                oldPersitentState={oldPersitentState}
                oldItems={oldItems}
                setData={setData}
            />
        );
    }

    return null;
};

export default ContinueWatchingMigrationContainer;
